import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fileMail, type UserPreferences } from '@/services/api/endpoints';
import { filesToDroppedMails, type DroppedMail } from '@/services/office/dragDrop';
import type { DropTarget } from '@/services/office/dropHitTest';
import {
  applyFiledCategories,
  resetCategoryProbes,
  FILED_CATEGORY_NAME,
} from '@/services/office/categories';
import { destinationProjectNumber } from '@/features/file-mail/destinationNumber';
import { createSourceDeleter } from '@/services/office/deleteFiledSource';
import { indexSelectionByMessageId, messageIdFromEmlBase64 } from '@/services/office/messageIdentity';
import { getEffectiveSelection } from '@/services/office/selection';
import { PREFERENCES_KEY } from '@/hooks/usePreferences';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useFilingStore } from '@/stores/filingStore';
import { useTreeStore } from '@/stores/treeStore';
import type { FileMailItemResult } from '@/features/file-mail/useFileMail';

/**
 * Central filing path shared by every drag-and-drop entry point (per-folder
 * native drop, the pane-level fallback drop, and the Office DragAndDropEvent).
 * A dropped message arrives as a base64 .eml/.msg blob with no itemId, so the
 * edge function parses the RFC Message-ID out of the content for dedup. That
 * same Message-ID is what lets us find the source mailbox item and tag it
 * « Rangé » (cf. services/office/messageIdentity.ts).
 *
 * Handles, in one place: the filing spinner (filingStore), the result toast,
 * cache invalidation so the new mail shows up, and auto-expanding the target
 * folder so the freshly filed mail is immediately visible.
 */
export function useDropFiler() {
  const qc = useQueryClient();
  const { showLoading, showResults, showError } = useFileMailToasts();
  const begin = useFilingStore((s) => s.begin);
  const end = useFilingStore((s) => s.end);
  const expand = useTreeStore((s) => s.expand);

  const fileMails = useCallback(
    async (mails: DroppedMail[], target: DropTarget | null): Promise<void> => {
      if (!target) {
        // Dropped outside any folder / mail / project → nothing to do. Silent
        // (no error toast): the user was never shown a valid drop affordance
        // there, so filing simply doesn't happen.
        addBreadcrumb({ category: 'office.event', message: 'drop-no-target' });
        return;
      }
      if (mails.length === 0) return;
      // filing-store key: the folder id, or the container id for a root drop.
      const filingKey = target.kind === 'folder' ? target.folderId : target.containerId;
      begin(filingKey);
      // Immediate spinner toast so the drop is visibly acknowledged while the
      // blob uploads/converts; updated in place to the result below.
      const toastId = showLoading(mails.length);
      const results: FileMailItemResult[] = [];

      // Préférences lues DANS le cache au moment du dépôt (cf. useFileMail) :
      // `undefined` — jamais chargées, ou back-end en panne — vaut « désactivée ».
      const prefs = qc.getQueryData<UserPreferences>(PREFERENCES_KEY);
      const deleteAfterFiling = prefs?.deleteAfterFiling ?? false;

      // JEU DE CATÉGORIES calculé UNE FOIS pour tout le lot : la cible du dépôt est
      // unique, donc le numéro d'affaire l'est aussi.
      //
      // Le projet est connu DIRECTEMENT pour un dépôt à la racine ; pour un dépôt
      // sur un dossier, la cible vient du DOM (`data-drop-folder-id`) et n'apporte
      // que l'identifiant du dossier — d'où la résolution par le cache des
      // dossiers, légitime ici : un dossier n'est une cible de dépôt que s'il est
      // rendu. Cf. destinationProjectNumber.
      const names: string[] = [];
      if (prefs?.autoCategorize) names.push(FILED_CATEGORY_NAME);
      if (prefs?.categorizeProjectNumber) {
        const number = await destinationProjectNumber(
          qc,
          target.kind === 'root'
            ? { containerId: target.containerId }
            : { folderId: target.folderId },
        );
        if (number) names.push(number);
      }
      const autoCategorize = names.length > 0;
      // Les deux options ont besoin de la MÊME chose : l'élément de la boîte dont
      // vient le blob. On ne la cherche donc que si l'une des deux est active.
      const needsSource = autoCategorize || deleteAfterFiling;

      // Sélection capturée MAINTENANT, avant la première mise en ligne : elle
      // reflète encore ce qui vient d'être glissé. La relire après le rangement,
      // plusieurs secondes de conversion et d'envoi plus tard, laisserait à
      // l'utilisateur le temps de cliquer ailleurs — le mail déposé ne serait
      // plus identifiable, et l'action échouerait sans raison visible.
      let sourceIndex: Map<string, string> | null = null;
      if (needsSource) {
        if (autoCategorize) resetCategoryProbes();
        try {
          sourceIndex = indexSelectionByMessageId(await getEffectiveSelection());
        } catch {
          // Sélection illisible (hôte sans multi-sélection, Office pas prêt) :
          // on rangera sans étiqueter ni supprimer.
          sourceIndex = null;
        }
      }

      /**
       * `itemId` EWS du mail d'origine du blob déposé, ou `null` si on ne peut pas
       * conclure. Voir services/office/messageIdentity.ts : la correspondance de
       * Message-ID est EXIGÉE, jamais devinée depuis la seule sélection.
       */
      const resolveSource = (dm: DroppedMail): string | null => {
        if (!sourceIndex || sourceIndex.size === 0) return null;
        // .msg écarté : format binaire (OLE2), dont les en-têtes ne sont pas
        // lisibles ici — le lecteur MSG ne vit que dans la fonction edge. C'est
        // aussi le format d'un fichier déposé depuis l'explorateur, cas où il n'y
        // a de toute façon aucun élément de la boîte à toucher.
        if (dm.format !== 'eml') return null;
        const messageId = messageIdFromEmlBase64(dm.contentBase64);
        const itemId = messageId ? sourceIndex.get(messageId) : undefined;
        if (!itemId) {
          // SILENCE VOLONTAIRE, et non un échec masqué : le blob ne correspond à
          // aucun mail sélectionné, donc il ne vient pas de la boîte (fichier
          // déposé depuis le disque). Annoncer « catégorie non appliquée » ou
          // « mail non supprimé » ferait passer un import de fichiers pour une
          // panne, alors qu'il n'y avait rien à étiqueter ni à supprimer.
          addBreadcrumb({
            category: 'office.event',
            message: 'drop-source-unresolved',
            data: { format: dm.format, hasMessageId: Boolean(messageId) },
          });
          return null;
        }
        return itemId;
      };

      /** Pose les catégories sur le mail d'origine. Renvoie le motif d'échec. */
      const categorizeDropped = async (itemId: string | null): Promise<string | undefined> => {
        if (!autoCategorize || !itemId) return undefined;
        try {
          await applyFiledCategories(itemId, names);
          return undefined;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          // Signalé, jamais remonté en exception : le rangement a réussi, et
          // c'est ce que l'utilisateur a demandé.
          addBreadcrumb({
            category: 'office.event',
            message: 'apply-category failed (drop)',
            data: { message },
          });
          return message;
        }
      };

      // Jeton mutualisé sur tout le lot, cf. createSourceDeleter.
      const deleteSource = createSourceDeleter(deleteAfterFiling);

      try {
        for (const dm of mails) {
          const label = dm.name ?? '(mail)';
          try {
            const resp = await fileMail({
              format: dm.format,
              contentBase64: dm.contentBase64,
              targetFolderId: target.kind === 'folder' ? target.folderId : undefined,
              targetContainerId: target.kind === 'root' ? target.containerId : undefined,
              metadata: {},
            });
            const sourceItemId = needsSource ? resolveSource(dm) : null;
            // Étiquetage APRÈS confirmation du serveur, et sur un DOUBLON aussi :
            // le mail est bel et bien rangé (il l'était déjà), donc l'étiquette
            // est vraie. Même raisonnement que la voie « Ranger ici ».
            const categoryError = await categorizeDropped(sourceItemId);
            if (resp.status === 'created') {
              // Suppression EN DERNIER : après l'étiquetage, pour que le mail
              // parte aux Éléments supprimés déjà marqué « Rangé » si les deux
              // options sont actives. Jamais sur un doublon.
              const deleteError = sourceItemId ? await deleteSource(sourceItemId) : undefined;
              results.push({
                itemId: label,
                subject: label,
                status: 'created',
                mailId: resp.mailId,
                categoryError,
                deleteError,
              });
            } else {
              results.push({
                itemId: label,
                subject: label,
                status: 'duplicate',
                existing: resp.existing,
                categoryError,
              });
            }
          } catch (err) {
            reportError({
              error: err instanceof Error ? err : new Error(String(err)),
              tags: ['dnd', 'file-one'],
            });
            results.push({
              itemId: label,
              subject: label,
              status: 'error',
              errorMessage: err instanceof Error ? err.message : String(err),
            });
          }
        }
        showResults(results, toastId);
        if (results.some((r) => r.status === 'created') && target.kind === 'folder') {
          expand(target.folderId);
        }
        if (target.kind === 'folder') {
          void qc.invalidateQueries({ queryKey: ['mails', target.folderId] });
        } else {
          void qc.invalidateQueries({ queryKey: ['mails', 'root', target.containerId] });
        }
        void qc.invalidateQueries({ queryKey: ['folders'] });
      } finally {
        end(filingKey);
      }
    },
    [qc, showLoading, showResults, begin, end, expand],
  );

  const fileFiles = useCallback(
    async (files: File[], target: DropTarget | null): Promise<void> => {
      if (files.length === 0) return;
      let mails: DroppedMail[];
      try {
        mails = await filesToDroppedMails(files);
      } catch (err) {
        reportError({
          error: err instanceof Error ? err : new Error(String(err)),
          tags: ['dnd', 'extract'],
        });
        showError(err instanceof Error ? err.message : String(err));
        return;
      }
      await fileMails(mails, target);
    },
    [fileMails, showError],
  );

  return { fileMails, fileFiles };
}
